import React from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import { RowExpansion } from "@design-system/ViewTable/RowExpansion";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { isGroupEndPeriod, isNLevelClassReport } from "@common/helpers/reports";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { GlTableCallbacks } from "../glDrillDown.lib";
import { CashFlowStatementViewValueCell } from "./CashFlowStatementViewValueCell";
import { getCashFlowStatementCellClassName } from "./cashFlowStatementCellClassName";
import { CashFlowStatementViewRow } from "./cashFlowStatementViewTypes";
import styles from "./CashFlowStatementTable.module.scss";

interface MakePeriodCellOptions {
    period: ReportPeriodWithLabel;
    periodIndex: number;
    allPeriods: ReportPeriodWithLabel[];
}

export const makeCashFlowStatementPeriodCell = ({
    period,
    periodIndex,
    allPeriods,
}: MakePeriodCellOptions) => {
    const isTotalPeriod = Boolean(period.isTotal);
    // N-level draws full-height separators at the table level, making the legacy
    // per-cell group-end divider redundant.
    const isNLevelClassMode = isNLevelClassReport(allPeriods);
    const isGroupEnd =
        !isTotalPeriod &&
        !isNLevelClassMode &&
        isGroupEndPeriod(allPeriods, periodIndex);

    const CashFlowStatementPeriodCell = ({
        row,
        table,
    }: CellContext<
        ViewTableFeatures,
        CashFlowStatementViewRow,
        unknown
    >): React.ReactElement => {
        const { values, accountCodes, isTotal, isSummary } = row.original;
        const onDrillDown = useViewTableMeta<GlTableCallbacks, unknown>(table)
            .callbacks?.onDrillDown;
        const value = values[periodIndex] ?? 0;

        return (
            <RowExpansion row={row}>
                {(isExpanded) => {
                    const shouldShowValues = !row.getCanExpand() || !isExpanded;
                    // Section headers show empty cells while expanded.
                    return shouldShowValues ? (
                        <CashFlowStatementViewValueCell
                            value={value}
                            accountCodes={
                                isTotalPeriod && period.comparisonDiff
                                    ? []
                                    : accountCodes
                            }
                            period={period}
                            isTotal={isTotal}
                            isSummary={isSummary}
                            isGroupEnd={isGroupEnd}
                            onDrillDown={onDrillDown}
                        />
                    ) : (
                        <ViewTableCell
                            className={classNames(
                                styles.numericCell,
                                getCashFlowStatementCellClassName({
                                    isTotal,
                                    isSummary,
                                }),
                            )}
                        />
                    );
                }}
            </RowExpansion>
        );
    };

    return CashFlowStatementPeriodCell;
};
